import { useCallback, useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import type { UseDisclosureReturn } from '../../hooks';
import { Dialog } from '../Dialog';
import { FormGroup } from '../Form/FormGroup';
import { Input } from '../Form/Input';

interface GenerateCustomerApiKeyDialogProps extends UseDisclosureReturn {
  title: ReactNode;
  label: ReactNode;
  confirmLabel: ReactNode;
  onGenerate: (label: string) => Promise<void>;
  usePortal?: boolean;
}

export function GenerateCustomerApiKeyDialog({
  title,
  label,
  confirmLabel,
  onGenerate,
  ...dialogProps
}: GenerateCustomerApiKeyDialogProps) {
  const [value, setValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (dialogProps.isOpen) {
      setValue('');
      setIsGenerating(false);
    }
  }, [dialogProps.isOpen]);

  const handleGenerate = useCallback(async () => {
    if (isGenerating) {
      return;
    }

    flushSync(() => setIsGenerating(true));
    try {
      await onGenerate(value);
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, onGenerate, value]);

  return (
    <Dialog
      {...dialogProps}
      dataTestId="generate-api-key-dialog"
      title={title}
      confirmLabel={confirmLabel}
      confirmLoading={isGenerating}
      preventClosing={isGenerating}
      width={400}
      onConfirm={handleGenerate}
    >
      <FormGroup label={label} mb="spacing16">
        <Input
          autoFocus
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          data-testid="generate-api-key-label"
        />
      </FormGroup>
    </Dialog>
  );
}
